const GUEST_CART_KEY = 'rayan_guest_cart';

/**
 * Элемент гостевой корзины в localStorage.
 * @typedef {{ product_id: string|number, quantity: number, name?: string, thumb?: string, price?: string|number, weight?: string, main_unit?: string }} GuestCartItem
 */

export const getGuestCartFromStorage = () => {
	try {
		const raw = localStorage.getItem(GUEST_CART_KEY);
		return raw ? JSON.parse(raw) : [];
	} catch {
		return [];
	}
};

/**
 * @param {GuestCartItem[]} items
 */
export const setGuestCartToStorage = (items) => {
	localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
};

export const clearGuestCartStorage = () => {
	localStorage.removeItem(GUEST_CART_KEY);
};

/**
 * Добавить или увеличить товар в гостевой корзине.
 * @param {{ product_id: string|number, quantity?: number, name?: string, thumb?: string, price?: string|number, weight?: string, main_unit?: string }} item
 * @param {number} quantity
 * @returns {GuestCartItem[]} новый массив
 */
export const addToGuestCartStorage = (item, quantity = 1) => {
	const items = getGuestCartFromStorage();
	const id = String(item.product_id);
	const existing = items.find((i) => String(i.product_id) === id);
	const qty = Number(quantity) || 1;

	const newItem = {
		product_id: item.product_id,
		quantity: existing ? existing.quantity + qty : qty,
		name: item.name ?? existing?.name ?? '',
		thumb: item.thumb ?? existing?.thumb ?? '',
		price: typeof item.price === 'string' ? item.price.replace(/\s/g, '') : (item.price ?? existing?.price ?? '0'),
		weight: item.weight ?? existing?.weight ?? '',
		main_unit: item.main_unit ?? existing?.main_unit ?? 'шт',
	};

	const next = existing
		? items.map((i) => (String(i.product_id) === id ? newItem : i))
		: [...items, newItem];

	setGuestCartToStorage(next);
	return next;
};

/**
 * Уменьшить количество или удалить.
 * @param {string|number} product_id
 * @param {number} quantity
 * @returns {GuestCartItem[]}
 */
export const reduceFromGuestCartStorage = (product_id, quantity = 1) => {
	const items = getGuestCartFromStorage();
	const id = String(product_id);
	const existing = items.find((i) => String(i.product_id) === id);
	if (!existing) return items;

	const nextQty = Math.max(0, existing.quantity - quantity);
	const next = nextQty === 0
		? items.filter((i) => String(i.product_id) !== id)
		: items.map((i) =>
				String(i.product_id) === id ? { ...i, quantity: nextQty } : i
		  );

	setGuestCartToStorage(next);
	return next;
};

/**
 * Удалить позицию по product_id (в гостевой корзине key = product_id).
 * @param {string|number} product_id
 * @returns {GuestCartItem[]}
 */
export const removeFromGuestCartStorage = (product_id) => {
	const items = getGuestCartFromStorage().filter(
		(i) => String(i.product_id) !== String(product_id)
	);
	setGuestCartToStorage(items);
	return items;
};

/**
 * Построить payload в формате ответа сервера корзины для Redux.
 * @param {GuestCartItem[]} items
 * @returns {{ products: object[], totals: object[], count: number }}
 */
export const buildGuestCartPayload = (items) => {
	if (!items || items.length === 0) {
		return {
			products: [],
			totals: [
				{ code: 'sub_total', text: '0', value: 0 },
				{ code: 'total', text: '0 ₽', value: 0 },
			],
			count: 0,
		};
	}

	const products = items.map((i) => {
		const price = parseFloat(String(i.price).replace(/\s/g, '')) || 0;
		const total = (price * i.quantity).toFixed(2);
		return {
			product_id: i.product_id,
			cart_id: i.product_id,
			name: i.name,
			thumb: i.thumb,
			quantity: i.quantity,
			price: String(price),
			total: total,
			weight: i.weight,
			main_unit: i.main_unit || 'шт',
			stock_qty: 999,
		};
	});

	const totalValue = products.reduce((s, p) => s + parseFloat(p.total), 0);
	const totalText = `${totalValue.toFixed(2).replace('.', ',')} ₽`;

	return {
		products,
		totals: [
			{ code: 'sub_total', text: totalText, value: totalValue },
			{ code: 'total', text: totalText, value: totalValue },
		],
		count: products.reduce((s, p) => s + p.quantity, 0),
	};
};
